import { useEffect, useMemo, useState } from 'react';

import {
  createPiClient,
  type ApStatus,
  type HealthResponse,
  type PiClient,
  type ScenarioListItem,
} from '../lib/piClient.js';

export type PiHealth = 'ok' | 'down' | 'unknown';

interface UsePiBackendResult {
  readonly client: PiClient;
  readonly health: PiHealth;
  readonly version: string | null;
  readonly apStatus: ApStatus | null;
  readonly scenarios: readonly ScenarioListItem[];
  readonly lastError: string | null;
  refreshScenarios(): Promise<void>;
}

const POLL_MS = 2000;

/**
 * SPEC §"Device + AP Mode UI" #2: poll /health and /ap/status every 2s
 * while the app is in Device + AP Mode. `enabled=false` cleanly idles
 * the polling timers (e.g. when the user switches back to Device Mode).
 */
export function usePiBackend(baseUrl: string, enabled: boolean): UsePiBackendResult {
  const client = useMemo(() => createPiClient(baseUrl), [baseUrl]);
  const [health, setHealth] = useState<PiHealth>('unknown');
  const [version, setVersion] = useState<string | null>(null);
  const [apStatus, setApStatus] = useState<ApStatus | null>(null);
  const [scenarios, setScenarios] = useState<readonly ScenarioListItem[]>([]);
  const [lastError, setLastError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) {
      setHealth('unknown');
      setVersion(null);
      setApStatus(null);
      setLastError(null);
      return;
    }
    let cancelled = false;
    const ctrl = new AbortController();

    const tick = async () => {
      try {
        const [h, s] = await Promise.all([
          client.health(ctrl.signal) as Promise<HealthResponse>,
          client.apStatus(ctrl.signal),
        ]);
        if (cancelled) return;
        setHealth('ok');
        setVersion(h.version);
        setApStatus(s);
        setLastError(null);
      } catch (cause) {
        if (cancelled || ctrl.signal.aborted) return;
        setHealth('down');
        setApStatus(null);
        setLastError(cause instanceof Error ? cause.message : String(cause));
      }
    };

    void tick();
    const id = window.setInterval(() => {
      void tick();
    }, POLL_MS);

    return () => {
      cancelled = true;
      ctrl.abort();
      window.clearInterval(id);
    };
  }, [client, enabled]);

  useEffect(() => {
    if (!enabled) {
      setScenarios([]);
      return;
    }
    let cancelled = false;
    const ctrl = new AbortController();
    void client
      .scenarios(ctrl.signal)
      .then((list) => {
        if (cancelled) return;
        setScenarios(list);
      })
      .catch((cause: unknown) => {
        if (cancelled || ctrl.signal.aborted) return;
        setLastError(cause instanceof Error ? cause.message : String(cause));
      });
    return () => {
      cancelled = true;
      ctrl.abort();
    };
  }, [client, enabled]);

  const refreshScenarios = async () => {
    try {
      const list = await client.scenarios();
      setScenarios(list);
    } catch (cause) {
      setLastError(cause instanceof Error ? cause.message : String(cause));
    }
  };

  return { client, health, version, apStatus, scenarios, lastError, refreshScenarios };
}
